import type {Rule} from 'antd/es/form';

import {CommonErrors} from '../../../constants/Common';
import type {
  LciFormValues,
  LogisticsRow,
  MaterialRow,
  ProductionDataValues
} from '../../../services/interface/pcfTypes';
import {
  A1MaterialsLabels,
  A3ManufacturingLabels,
  LogisticsLabels
} from '../PCFKeyLabels';

import {
  isProductionDataComplete,
  siteUnitsSumMatchesTotal
} from './lciProductionUtils';
import {LCI_SUBTAB_ORDER, type LciSubTabId} from './pcfFormValidation';

const parseNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = parseFloat(String(value));

  return Number.isFinite(parsed) ? parsed : null;
};

export const positiveUnitsRule = (label: string): Rule => ({
  validator: async (_, value) => {
    const parsed = parseNumber(value);

    if (parsed == null || parsed <= 0) {
      return await Promise.reject(new Error(CommonErrors.mandatory(label)));
    }

    await Promise.resolve();
  }
});

export const siteUnitsSumRule = (): Rule => ({
  validator: async (_, production: ProductionDataValues) => {
    if (!production || production.setup === 'single_site') {
      await Promise.resolve();

      return;
    }

    if (!siteUnitsSumMatchesTotal(production)) {
      return await Promise.reject(
        new Error(
          'Units per site must sum to total units produced before continuing to A3.'
        )
      );
    }

    await Promise.resolve();
  }
});

export const allocationFactorRule = (): Rule => ({
  validator: async (_, production: ProductionDataValues) => {
    if (!production?.sites?.length) {
      await Promise.resolve();

      return;
    }

    for (const site of production.sites) {
      const units = parseNumber(site.unitsProduced);
      const throughput = parseNumber(site.facilityThroughput);

      if (units != null && throughput != null && units > throughput) {
        return await Promise.reject(
          new Error(
            'Based on your inputs, the share attributed to this product exceeds 100%. ' +
              'Please check your total facility throughput figure.'
          )
        );
      }
    }

    await Promise.resolve();
  }
});

export const supplierEfValueRules = (getSupplierEf: () => string): Rule[] => [
  {
    validator: async (_, value) => {
      if (getSupplierEf() !== 'yes_enter') {
        await Promise.resolve();

        return;
      }

      const parsed = parseNumber(value);

      if (parsed == null || parsed < 0) {
        return await Promise.reject(
          new Error(CommonErrors.mandatory(A1MaterialsLabels.SUPPLIER_EF_VALUE))
        );
      }

      await Promise.resolve();
    }
  }
];

export const supplierEfSourceRules = (getSupplierEf: () => string): Rule[] => [
  {
    validator: async (_, value) => {
      if (getSupplierEf() !== 'yes_enter') {
        await Promise.resolve();

        return;
      }

      if (!String(value ?? '').trim()) {
        return await Promise.reject(
          new Error(
            CommonErrors.mandatory(A1MaterialsLabels.SUPPLIER_EF_SOURCE)
          )
        );
      }

      await Promise.resolve();
    }
  }
];

export const ltlLoadPercentRules = (getLoadFactor: () => string): Rule[] => [
  {
    validator: async (_, value) => {
      if (getLoadFactor() !== 'ltl') {
        await Promise.resolve();

        return;
      }

      const parsed = parseNumber(value);

      if (parsed == null || parsed <= 0 || parsed > 100) {
        return await Promise.reject(
          new Error(CommonErrors.mandatory(LogisticsLabels.LTL_LOAD_PERCENT))
        );
      }

      await Promise.resolve();
    }
  }
];

export const disposalPercentSumRule = (): Rule => ({
  validator: async (_, routes: LciFormValues['c']['disposalRoutes']) => {
    if (!routes?.length) {
      await Promise.resolve();

      return;
    }

    const total = routes.reduce((sum, row) => {
      const percent = parseNumber(row.percent);

      return sum + (percent ?? 0);
    }, 0);

    if (Math.abs(total - 100) > 0.01) {
      return await Promise.reject(
        new Error('End-of-life route percentages must sum to 100%.')
      );
    }

    await Promise.resolve();
  }
});

export const noWasteJustificationRule = (
  getWasteTypes: () => string[]
): Rule[] => [
  {
    validator: async (_, value) => {
      const selected = getWasteTypes();

      if (selected.length > 0) {
        await Promise.resolve();

        return;
      }

      if (!String(value ?? '').trim()) {
        return await Promise.reject(
          new Error(
            CommonErrors.mandatory(A3ManufacturingLabels.NO_WASTE_JUSTIFICATION)
          )
        );
      }

      await Promise.resolve();
    }
  }
];

export type LciContinueValidationTab =
  | 'prod'
  | 'a1'
  | 'a2'
  | 'a3'
  | 'a4'
  | 'b'
  | 'c';

export interface LciContinueValidation {
  message: string;
  tab: LciContinueValidationTab;
  errorFields?: Array<{
    name: Array<string | number>;
    errors: string[];
  }>;
  facilityIndex?: number;
}

export const validateLciProdTab = (
  values: LciFormValues
): LciContinueValidation | null => {
  if (!isProductionDataComplete(values.production)) {
    return {
      message:
        'Complete Production Data before continuing — site units and allocation must be filled in correctly.',
      tab: 'prod'
    };
  }

  if (!siteUnitsSumMatchesTotal(values.production)) {
    return {
      message: 'Units per site must sum to total units produced.',
      tab: 'prod'
    };
  }

  return null;
};

export const validateLciA1Tab = (
  values: LciFormValues
): LciContinueValidation | null => {
  for (const material of values.a1?.materials ?? []) {
    if (material.supplierEF === 'yes_enter') {
      if (
        parseNumber(material.supplierEfValue) == null ||
        !String(material.supplierEfSource ?? '').trim()
      ) {
        return {
          message:
            'Enter supplier emission factor value and source for all materials where supplier EF is provided.',
          tab: 'a1'
        };
      }
    }
  }

  return null;
};

export const validateLciA2Tab = (
  values: LciFormValues
): LciContinueValidation | null => {
  for (const item of values.a2?.items ?? []) {
    if (item.loadFactor === 'ltl') {
      const percent = parseNumber(item.ltlLoadPercent);

      if (percent == null || percent <= 0 || percent > 100) {
        return {
          message:
            'Enter a valid truck load share for all LTL inbound transport legs.',
          tab: 'a2'
        };
      }
    }
  }

  return null;
};

export const validateLciA3Tab = (
  values: LciFormValues
): LciContinueValidation | null => {
  const facilities = values.a3?.facilities ?? [];
  const setup = values.production?.setup ?? 'single_site';
  const isMultiSite =
    setup === 'multiple_sites' || setup === 'contract_manufacturer';

  const configuredFacilities = facilities
    .map((facility, index) => ({facility, index}))
    .filter(({facility, index}) =>
      isMultiSite ? Boolean(facility.configured) : index === 0
    );

  if (isMultiSite && configuredFacilities.length === 0) {
    return {
      message: 'Configure A3 data for at least one manufacturing site.',
      tab: 'a3'
    };
  }

  for (const {facility, index} of configuredFacilities) {
    const units = parseNumber(
      values.production.sites?.find((site) => site.name === facility.name)
        ?.unitsProduced ?? values.production.units
    );
    const throughput = parseNumber(facility.facilityThroughput);

    if (units != null && throughput != null && units > throughput) {
      return {
        message: `Allocation factor exceeds 100% at ${facility.name}. Check facility throughput.`,
        tab: 'a3',
        facilityIndex: index
      };
    }

    if (
      facility.hasProcessEmissions === 'yes' &&
      !facility.processAllocationScope
    ) {
      return {
        message: `Select process emission scope for ${facility.name}.`,
        tab: 'a3',
        facilityIndex: index
      };
    }

    if (
      !facility.wasteTypesSelected?.length &&
      !String(facility.noWasteJustification ?? '').trim()
    ) {
      const message =
        'In A3 Manufacturing → Waste & Emissions to Environment, select waste types or provide a ' +
        `no-waste justification for ${facility.name}.`;

      return {
        message,
        tab: 'a3',
        facilityIndex: index,
        errorFields: [
          {
            name: ['a3', 'facilities', index, 'wasteTypesSelected'],
            errors: [message]
          },
          {
            name: ['a3', 'facilities', index, 'noWasteJustification'],
            errors: [message]
          }
        ]
      };
    }
  }

  return null;
};

export const validateLciA4Tab = (
  values: LciFormValues
): LciContinueValidation | null => {
  for (const item of values.a4?.items ?? []) {
    if (item.loadFactor === 'ltl') {
      const percent = parseNumber(item.ltlLoadPercent);

      if (percent == null || percent <= 0 || percent > 100) {
        return {
          message:
            'Enter a valid truck load share for all LTL outbound transport legs.',
          tab: 'a4'
        };
      }
    }
  }

  return null;
};

export const validateLciBTab = (
  values: LciFormValues
): LciContinueValidation | null => {
  if (values.b?.usesEnergyInUse && values.b.usesEnergyInUse !== 'no') {
    if (
      parseNumber(values.b.annualEnergyPerUnit) == null ||
      parseNumber(values.b.serviceLife) == null
    ) {
      return {
        message:
          'Enter annual energy use and service life when the product consumes energy during use.',
        tab: 'b'
      };
    }
  }

  return null;
};

export const validateLciCTab = (
  values: LciFormValues
): LciContinueValidation | null => {
  const routes = values.c?.disposalRoutes ?? [];

  if (routes.length > 0) {
    const total = routes.reduce((sum, row) => {
      const percent = parseNumber(row.percent);

      return sum + (percent ?? 0);
    }, 0);

    if (Math.abs(total - 100) > 0.01) {
      return {
        message: 'End-of-life route percentages must sum to 100%.',
        tab: 'c'
      };
    }
  }

  return null;
};

const LCI_TAB_VALIDATORS: Record<
  LciSubTabId,
  (values: LciFormValues) => LciContinueValidation | null
> = {
  prod: validateLciProdTab,
  a1: validateLciA1Tab,
  a2: validateLciA2Tab,
  a3: validateLciA3Tab,
  a4: validateLciA4Tab,
  b: validateLciBTab,
  c: validateLciCTab
};

export const validateLciTabBeforeContinue = (
  values: LciFormValues,
  tab: LciSubTabId
): LciContinueValidation | null => LCI_TAB_VALIDATORS[tab](values);

export const validateLciBeforeContinue = (
  values: LciFormValues
): LciContinueValidation | null => {
  for (const tab of LCI_SUBTAB_ORDER) {
    const result = LCI_TAB_VALIDATORS[tab](values);

    if (result) {
      return result;
    }
  }

  return null;
};

export const normalizeLegacyLciValues = (
  lci: Partial<LciFormValues> | undefined
): LciFormValues => {
  const production: ProductionDataValues = {
    setup: 'single_site',
    period: 'annual',
    year: '2025',
    units: '',
    sites: [],
    allocationBasis: '',
    ...(lci?.production ?? {})
  };

  const mapLogisticsItem = (item: Record<string, unknown>): LogisticsRow => ({
    material: String(item.material ?? ''),
    supplierCity: String(item.supplierCity ?? ''),
    destinationCity: String(item.destinationCity ?? ''),
    mode: String(item.mode ?? 'road_truck_diesel'),
    distance: (item.distance ?? '') as string | number,
    annualQuantity: (item.annualQuantity ?? item.load ?? '') as string | number,
    loadFactor: String(item.loadFactor ?? 'ftl'),
    ltlLoadPercent: (item.ltlLoadPercent ?? 50) as string | number,
    mixedModeSplit:
      (item.mixedModeSplit as Record<string, number | string>) ?? {},
    ef: (item.ef ?? '') as string | number,
    returnJourney: Boolean(item.returnJourney)
  });

  const mapMaterial = (item: Record<string, unknown>): MaterialRow => ({
    name: String(item.name ?? ''),
    qty: (item.qty ?? '') as string | number,
    unit: String(item.unit ?? 'kg'),
    sourcing: String(item.sourcing ?? 'india_national'),
    supplierEF: String(item.supplierEF ?? 'no_database_default'),
    supplierEfValue: (item.supplierEfValue ?? '') as string | number,
    supplierEfSource: String(item.supplierEfSource ?? ''),
    emissionFactor: (item.emissionFactor ?? '') as string | number,
    confidence: String(item.confidence ?? 'bom_system')
  });

  return {
    production,
    a1: {
      materials: (lci?.a1?.materials ?? []).map((row) =>
        mapMaterial(row as unknown as Record<string, unknown>)
      ),
      packaging: {
        primary: false,
        secondary: false,
        tertiary: false,
        lubricants: false,
        none: false,
        notSure: false,
        ...(lci?.a1?.packaging ?? {})
      }
    },
    a2: {
      items: (lci?.a2?.items ?? []).map((row) =>
        mapLogisticsItem(row as unknown as Record<string, unknown>)
      )
    },
    a3: lci?.a3 ?? {
      facilities: [],
      activeFacility: null
    },
    a4: {
      shipTo: lci?.a4?.shipTo ?? '',
      items: (lci?.a4?.items ?? []).map((row) =>
        mapLogisticsItem(row as unknown as Record<string, unknown>)
      )
    },
    b: {
      usesEnergyInUse: '',
      energyType: '',
      annualEnergyPerUnit: '',
      energyUnit: '',
      serviceLife: '',
      serviceLifeUnit: 'years',
      hasConsumables: 'no',
      consumables: [],
      items: [],
      ...(lci?.b ?? {})
    },
    c: {
      disposalRoutes: [],
      disposalTransport: '',
      hasRecoverableValue: 'no',
      recoverableType: '',
      recoverableFraction: '',
      items: [],
      ...(lci?.c ?? {})
    }
  };
};
